import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Chip,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import DeleteIcon from '@mui/icons-material/Delete'
import { useNotes } from '@/hooks/useNotes'
import { useContentBundle } from '@/hooks/useContentBundle'
import { useAppStore } from '@/store/appStore'
import { extractApiErrorMessage } from '@/utils/apiError'
import { useGuardedNavigate } from '@/hooks/useGuardedNavigate'

export function NotesManager() {
  const { t } = useTranslation()
  const navigate = useGuardedNavigate()
  const { setOverlayPage } = useAppStore()
  const { notes, deleteNote } = useNotes()
  const { data: bundle, isLoading } = useContentBundle()
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null)
  const [deleting, setDeleting] = useState(false)

  // Group notes by PracticeArea, then Practice
  const grouped = useMemo(() => {
    if (!bundle) return []

    const paNotesMap = new Map<number, { paNote: (typeof notes)[number] | null; practiceNotes: { practice: (typeof bundle.practices)[number]; note: (typeof notes)[number] }[] }>()

    // Seed map with PAs that have notes
    for (const note of notes) {
      if (note.contentType === 'PracticeArea') {
        const pa = bundle.practiceAreas.find((p) => p.id === note.entityId)
        if (!pa) continue
        if (!paNotesMap.has(pa.id)) {
          paNotesMap.set(pa.id, { paNote: null, practiceNotes: [] })
        }
        paNotesMap.get(pa.id)!.paNote = note
      }
    }

    // Add practice notes, creating a PA entry if one doesn't exist yet
    for (const note of notes) {
      if (note.contentType === 'Practice') {
        const practice = bundle.practices.find((p) => p.id === note.entityId)
        if (!practice) continue
        const paId = practice.practiceAreaId
        if (!paNotesMap.has(paId)) {
          paNotesMap.set(paId, { paNote: null, practiceNotes: [] })
        }
        paNotesMap.get(paId)!.practiceNotes.push({ practice, note })
      }
    }

    // Build sorted result: sort PAs by their sortOrder, practices within by sortOrder
    return bundle.practiceAreas
      .filter((pa) => paNotesMap.has(pa.id))
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((pa) => {
        const entry = paNotesMap.get(pa.id)!
        const sortedPracticeNotes = entry.practiceNotes
          .slice()
          .sort((a, b) => a.practice.sortOrder - b.practice.sortOrder)
        return {
          pa,
          paNote: entry.paNote,
          practiceNotes: sortedPracticeNotes,
          totalNoteCount:
            (entry.paNote ? 1 : 0) + sortedPracticeNotes.length,
        }
      })
  }, [notes, bundle])

  const handleDeleteClick = (e: React.MouseEvent, noteId: number) => {
    e.stopPropagation()
    setConfirmDeleteId(noteId)
  }

  const handleConfirmDelete = async () => {
    if (confirmDeleteId !== null) {
      setDeleting(true)
      try {
        await deleteNote(confirmDeleteId)
        setConfirmDeleteId(null)
      } catch (err) {
        toast.error(extractApiErrorMessage(err) || t('notes.noteDeleteFailed'))
      } finally {
        setDeleting(false)
      }
    }
  }

  const handleCancelDelete = () => {
    setConfirmDeleteId(null)
  }

  const handleNavigateToPracticeArea = (paId: number) => {
    setOverlayPage(null)
    navigate(`/practice-area/${paId}`)
  }

  const handleNavigateToPractice = (practiceId: number) => {
    setOverlayPage(null)
    navigate(`/practice/${practiceId}`)
  }

  if (isLoading) {
    return (
      <Box sx={{ pt: 3, textAlign: 'center' }}>
        <Typography color="text.secondary">{t('pages.loading')}</Typography>
      </Box>
    )
  }

  if (!bundle) {
    return (
      <Box sx={{ pt: 3, textAlign: 'center' }}>
        <Typography color="text.secondary">{t('content.unavailable')}</Typography>
      </Box>
    )
  }

  if (grouped.length === 0) {
    return (
      <Box sx={{ pt: 3, textAlign: 'center' }}>
        <Typography color="text.secondary">{t('notes.noNotesYet')}</Typography>
      </Box>
    )
  }

  return (
    <>
      <Box>
        {grouped.map(({ pa, paNote, practiceNotes, totalNoteCount }) => (
          <Accordion key={pa.id} disableGutters elevation={0} sx={{ border: '1px solid', borderColor: 'divider', mb: 1, '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, mr: 1 }}>
                <Typography variant="body1" fontWeight={600}>
                  {pa.displayName || pa.name}
                </Typography>
                <Chip
                  label={t('notes.noteCount', { count: totalNoteCount })}
                  size="small"
                  variant="outlined"
                  color="primary"
                />
              </Box>
            </AccordionSummary>

            <AccordionDetails sx={{ pt: 0 }}>
              {/* PA-level note */}
              {paNote && (
                <Box sx={{ mb: practiceNotes.length > 0 ? 2 : 0, p: 1.5, backgroundColor: 'action.hover', borderRadius: 1, position: 'relative' }}>
                  <Tooltip title={t('notes.deleteNote')}>
                    <IconButton
                      size="small"
                      onClick={(e) => handleDeleteClick(e, paNote.id)}
                      sx={{ position: 'absolute', top: 4, right: 4 }}
                      aria-label={t('notes.deleteNote')}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                    {t('notes.title')}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}
                  >
                    {paNote.body}
                  </Typography>
                </Box>
              )}

              {/* Navigate to PA */}
              <Box sx={{ mb: practiceNotes.length > 0 ? 2 : 0 }}>
                <Button
                  size="small"
                  variant="text"
                  endIcon={<OpenInNewIcon fontSize="small" />}
                  onClick={() => handleNavigateToPracticeArea(pa.id)}
                >
                  {t('notes.navigateToPracticeArea')}
                </Button>
              </Box>

              {/* Practice-level notes */}
              {practiceNotes.map(({ practice, note }) => (
                <Accordion
                  key={practice.id}
                  disableGutters
                  elevation={0}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    mb: 0.5,
                    '&:before': { display: 'none' },
                    backgroundColor: 'background.default',
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="body2" fontWeight={500}>
                      {practice.displayName || practice.name}
                    </Typography>
                  </AccordionSummary>

                  <AccordionDetails sx={{ pt: 0 }}>
                    <Box sx={{ mb: 1, p: 1.5, backgroundColor: 'action.hover', borderRadius: 1, position: 'relative' }}>
                      <Tooltip title={t('notes.deleteNote')}>
                        <IconButton
                          size="small"
                          onClick={(e) => handleDeleteClick(e, note.id)}
                          sx={{ position: 'absolute', top: 4, right: 4 }}
                          aria-label={t('notes.deleteNote')}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                        {t('notes.title')}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}
                      >
                        {note.body}
                      </Typography>
                    </Box>

                    <Button
                      size="small"
                      variant="text"
                      endIcon={<OpenInNewIcon fontSize="small" />}
                      onClick={() => handleNavigateToPractice(practice.id)}
                    >
                      {t('notes.navigateToPractice')}
                    </Button>
                  </AccordionDetails>
                </Accordion>
              ))}
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>

      {/* Delete confirmation dialog */}
      <Dialog open={confirmDeleteId !== null} onClose={handleCancelDelete}>
        <DialogTitle>{t('notes.confirmDeleteTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('notes.confirmDeleteMessage')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} disabled={deleting}>{t('notes.cancel')}</Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {deleting ? t('notes.saving') : t('notes.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
